import React, { useEffect, useState, useContext } from 'react';
import ReactPixel from 'react-facebook-pixel';

// MUI
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Stack,
} from '@mui/material';

// Services
import userService from '../../services/userService';
import dictionaryService from '../../services/dictionaryService';

// Components
import Icon from '../../components/Elements/Icon';

// Import Context
import { UserContext } from '../../context/User';
import SessionContext from '../../context/Session';

// Build Component
const NewUserWelcome = ({ userDetails }) => {
  const { user, setUpdateUser } = useContext(UserContext) || {};
  const { visitedWelcome, setVisitedWelcome, welcomeValidationTrigger } =
    useContext(SessionContext) || {};
  const [error, setError] = useState(null);
  const [aboutUsDict, setAboutUsDict] = useState([]);
  const [userData, setUserData] = useState({
    first_name: userDetails?.first_name || user?.first_name || '',
    last_name: userDetails?.last_name || user?.last_name || '',
    id: userDetails?.id || user?.id || '',
    about_us: '',
    about_us_text: '',
  });
  const [tagged, setTagged] = useState(false);

  const updateUser = async () => {
    try {
      let isDataComplete = await checkMissingData();
      if (isDataComplete) {
        let data = userData;
        let userUpdate = await userService.updateProfileFields(data);
        setUpdateUser('basic');
        if (userUpdate) {
          window.location.href = `/app/freeTrialActivated`;
        }
      }
    } catch (error) {
      setError(error.message);
    }
  };

  const checkMissingData = async () => {
    if (!userData.first_name || !userData.last_name || !userData.about_us) {
      setError('Please make sure all required fields are filled out.');
      return false;
    } else {
      setError(null);
      return true;
    }
  };

  const updateAboutUs = async (value) => {
    setUserData({ ...userData, about_us: value });
  };

  useEffect(() => {
    const fetchData = async () => {
      let getAboutUs = await dictionaryService.fetchDictionary('about_us');
      getAboutUs.sort((a, b) => (a.order ? a.order - b.order : 0));
      setAboutUsDict(getAboutUs);
    };

    if (userDetails || user) {
      fetchData();
    }

    setVisitedWelcome(true);
  }, []);

  useEffect(() => {
    const sourceId = userDetails?.id || user?.id;
    if (!sourceId) return;
    const needsId = !userData?.id;
    const needsFirstName = !userData?.first_name;
    const needsLastName = !userData?.last_name;
    if (needsId || needsFirstName || needsLastName) {
      let useUser = userDetails || user;
      setUserData((prev) => ({
        ...prev,
        id: prev.id || useUser?.id,
        first_name: prev.first_name || useUser?.first_name || '',
        last_name: prev.last_name || useUser?.last_name || '',
      }));
    }
  }, [userDetails, user]);

  useEffect(() => {
    if (welcomeValidationTrigger) {
      checkMissingData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeValidationTrigger]);

  useEffect(() => {
    if (error) {
      checkMissingData();
    }
    if (!tagged && userData && userData.id) {
      /*
      gtag('event', 'user_registration', {
        user_id: userData.id,
      });
      */
      if (
        !window.location.href.includes('demo') &&
        !window.location.href.includes('localhost') &&
        userData.email
      ) {
        ReactPixel.init('234261399680296');
        ReactPixel.track('CompleteRegistration', {
          email: userData.email,
        });
      }
      setTagged(true);
    }
  }, [userData]);

  const showError = !!error;

  return (
    <Box sx={{ p: 4 }}>
      <Typography
        sx={{ fontSize: 28, fontWeight: 800, textAlign: 'center', mb: 2, lineHeight: 1.2 }}
      >
        Welcome to ShotKraft! <Icon icon="Wave" size={35} color="Yellow" />
      </Typography>

      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Typography sx={{ textAlign: 'center', color: 'text.secondary', mb: 2 }}>
          Let's get to know you
        </Typography>

        <Stack spacing={2}>
          <TextField
            label="First Name"
            placeholder="i.e. Allan"
            value={userData.first_name}
            onChange={(e) => setUserData({ ...userData, first_name: e.target.value })}
            error={showError && !userData.first_name}
            helperText={showError && !userData.first_name ? 'Required' : ' '}
            fullWidth
            size="small"
          />

          <TextField
            label="Last Name"
            placeholder="i.e. Smithy"
            value={userData.last_name}
            onChange={(e) => setUserData({ ...userData, last_name: e.target.value })}
            error={showError && !userData.last_name}
            helperText={showError && !userData.last_name ? 'Required' : ' '}
            fullWidth
            size="small"
          />

          <FormControl
            fullWidth
            size="small"
            error={showError && !userData.about_us}
            sx={{ textAlign: 'left' }}
          >
            <InputLabel id="about-us-label">How did you hear about us</InputLabel>
            <Select
              labelId="about-us-label"
              label="How did you hear about us"
              value={userData.about_us}
              onChange={(e) => updateAboutUs(e.target.value)}
            >
              {aboutUsDict.map((item) => (
                <MenuItem key={item?.id ?? item?.dict_about_us} value={item.dict_about_us}>
                  {item.dict_about_us}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{showError && !userData.about_us ? 'Required' : ' '}</FormHelperText>
          </FormControl>

          <TextField
            label="More details (optional)"
            placeholder="Can you give more specifics: What was your search query on google? Which social media? Etc. (optional, but highly appreciated)"
            value={userData.about_us_text}
            onChange={(e) => setUserData({ ...userData, about_us_text: e.target.value })}
            fullWidth
            multiline
            minRows={3}
            size="small"
          />

          {error ? (
            <Typography sx={{ color: 'error.main', textAlign: 'center' }}>{error}</Typography>
          ) : null}

          <Button
            variant="contained"
            fullWidth
            onClick={() => updateUser()}
            sx={{ height: 45, fontSize: 18, fontWeight: 700, textTransform: 'none' }}
          >
            Next
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};

export default NewUserWelcome;
